import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";

import type { DictTreeItemVO, StatusEnum } from "@/api/system/dictionaries/type";
import { dictionariesAtom, dictReadyAtom, refreshDictionariesAtom } from "@/store/dict";

/** 下拉选项结构 */
export interface DictOption {
  label: string;
  value: string;
  /** 字典项启用状态，无字段时视为启用 */
  statusEnabled?: StatusEnum;
}

/**
 * 将字典树规范为可安全遍历的数组
 * @param source jotai 中的字典树，可能为 null/undefined
 */
const asDictList = (source: DictTreeItemVO[] | null | undefined): DictTreeItemVO[] =>
  Array.isArray(source) ? source : [];

/**
 * 消费全局字典：下拉选项、值转标签、手动刷新
 */
export const useDict = () => {
  const dictionaries = useAtomValue(dictionariesAtom);
  const dictReady = useAtomValue(dictReadyAtom);
  const refresh = useSetAtom(refreshDictionariesAtom);
  // 兜底：避免接口返回 null 时对 dictionaries 直接 find
  const dictList = asDictList(dictionaries);

  /** 按字典类型编码取顶层节点 */
  const getDictType = useCallback(
    (typeCode: string): DictTreeItemVO | undefined =>
      dictList.find((item) => item.dictCode === typeCode),
    [dictList],
  );

  /** 取某字典类型下的 Select options */
  const getDictOptions = useCallback(
    (typeCode: string): DictOption[] => {
      const type = dictList.find((item) => item.dictCode === typeCode);
      return (type?.children ?? []).map((item) => ({
        label: item.dictName,
        value: item.dictCode,
        statusEnabled: item.statusEnabled,
      }));
    },
    [dictList],
  );

  /** 字典项编码转展示文案，找不到时回退原值 */
  const getDictLabel = useCallback(
    (typeCode: string, value?: string | null): string => {
      if (!value) return "—";
      const type = dictList.find((item) => item.dictCode === typeCode);
      const item = (type?.children ?? []).find((child) => child.dictCode === value);
      return item?.dictName ?? value;
    },
    [dictList],
  );

  return {
    dictionaries: dictList,
    dictReady,
    refresh,
    getDictType,
    getDictOptions,
    getDictLabel,
  };
};
